<template>
  <div>
    <header class="modal-header py25 px65 h1 serif weight-700 bg-cl-secondary">
      <i
        slot="close"
        class="modal-close material-icons p15 cl-bg-tertiary"
        @click="close"
      >
        close
      </i>
      {{ !isLoggedIn ? $t('Register') : $t('Verify phone number') }}
    </header>

    <div class="modal-content pt30 pb60 px65 cl-secondary">
      <template v-if="!isLoggedIn">
        <form @submit.prevent="register" novalidate class="relative">
          <base-input
            class="mb10"
            type="email"
            name="email"
            autocomplete="email"
            v-model="email"
            @blur="$v.email.$touch()"
            focus
            :placeholder="$t('E-mail address *')"
            :validations="[
              {
                condition: !$v.email.required && $v.email.$error,
                text: $t('Field is required.')
              },
              {
                condition: !$v.email.email && $v.email.$error,
                text: $t('Please provide valid e-mail address.')
              }
            ]"
          />
          <div class="row mb10">
            <base-input
              class="col-xs-6"
              type="text"
              name="fist-name"
              autocomplete="given-name"
              v-model="firstName"
              @blur="$v.firstName.$touch()"
              :placeholder="$t('First name *')"
              :validations="[
                {
                  condition: !$v.firstName.required && $v.firstName.$error,
                  text: $t('Field is required.')
                },
                {
                  condition: !$v.firstName.minLength,
                  text: $t('Name must have at least 2 letters.')
                }
              ]"
            />
            <base-input
              class="col-xs-6"
              type="text"
              name="last-name"
              autocomplete="family-name"
              v-model="lastName"
              @blur="$v.lastName.$touch()"
              :placeholder="$t('Last name *')"
              :validations="[{
                condition: !$v.lastName.required && $v.lastName.$error,
                text: $t('Field is required.')
              }]"
            />
          </div>
          <div class="row tel-input mb10">
            <base-telephone-input
              class="col-xs-12"
              :required="true"
              :validations="phoneValidations"
              :value="phoneNumber"
              @update="updatePhoneNumber"
              @input="$v.phoneNumber.$touch()"
            />
          </div>
          <base-input
            class="mb10"
            type="password"
            name="password"
            ref="password"
            autocomplete="new-password"
            v-model="password"
            @blur="$v.password.$touch()"
            :placeholder="$t('Password *')"
            :validations="[
              {
                condition: !$v.password.required && $v.password.$error,
                text: $t('Field is required.')
              },
              {
                condition: !$v.password.minLength && $v.password.$error,
                text: $t('Password must have at least 8 letters.')
              }
            ]"
          />
          <base-input
            class="mb10"
            type="password"
            name="password-confirm"
            autocomplete="new-password"
            v-model="rPassword"
            @blur="$v.rPassword.$touch()"
            :placeholder="$t('Repeat password *')"
            :validations="[
              {
                condition: !$v.rPassword.required && $v.rPassword.$error,
                text: $t('Field is required.')
              },
              {
                condition: !$v.rPassword.sameAsPassword && $v.rPassword.$error,
                text: $t('Passwords must be identical.')
              }
            ]"
          />
          <base-checkbox
            class="mb10"
            id="terms"
            v-model="conditions"
            @blur="$v.conditions.$reset()"
            @change="$v.conditions.$touch()"
            :validations="[{
              condition: !$v.conditions.required && $v.conditions.$error,
              text: $t('You must accept the terms and conditions.')
            }]"
          >
            {{ $t('I agree to') }}
            <span class="link pointer" @click.prevent="openModal">
              {{ $t('Terms and conditions') }}
            </span>
          </base-checkbox>
          <button-full class="mb20" type="submit">
            {{ $t('Register an account') }}
          </button-full>
          <div class="center-xs">
            <span>
              {{ $t('or') }}
              <a href="#" @click.prevent="switchElem">
                {{ $t('login to your account') }}
              </a>
            </span>
          </div>
        </form>
        <social-login />
      </template>
      <template v-else>
        <form @submit.prevent="verifyPhoneNumber" novalidate>
          <base-input
            class="mb10"
            type="text"
            name="sms-verification-code"
            v-model="verificationCode"
            @blur="$v.verificationCode.$touch()"
            :placeholder="$t('Verification Code')"
            :validations="[
              {
                condition: !$v.verificationCode.required && $v.verificationCode.$error,
                text: $t('Field is required.')
              },
              {
                condition: !$v.verificationCode.minLength,
                text: $t('Code consists of 4 digits')
              }
            ]"
          />

          <div class="center-md center-xs">
            <timer @endTimer="enableSendNewSMS = true"/>
            <div :class="{'disabled': !enableSendNewSMS}" class="send-sms" @click="sendSMSAgain">{{ $t('Resend code') }}</div>
          </div>
          <button-full class="mb20" type="submit">
            {{ $t('Verify') }}
          </button-full>
        </form>
      </template>
    </div>
    <modal-terms-of-use v-if="termsRequested" :showModal="showModal" @hook:mounted="openModal" />
  </div>
</template>
<script>
import Register from '@vue-storefront/core/compatibility/components/blocks/Auth/Register'
import ButtonFull from 'theme/components/theme/ButtonFull.vue'
import BaseCheckbox from 'theme/components/core/blocks/Form/BaseCheckbox.vue'
import SocialLogin from './SocialLogin'
import { required, email, minLength, sameAs } from 'vuelidate/lib/validators'
import { mapGetters, mapActions } from 'vuex'
import { PersonalDetails } from '@vue-storefront/core/modules/checkout/components/PersonalDetails'
import Timer from "../MyAccount/Timer";

const BaseInput = () => import(/* webpackChunkName: "vsf-base-input" */ 'theme/components/core/blocks/Form/BaseInput');
const BaseTelephoneInput = () => import(/* webpackChunkName: "vsf-base-telephone-input" */ 'theme/components/core/blocks/Form/BaseTelephoneInput.vue');
const ModalTermsOfUse = () => import(/* webpackChunkName: "vsf-modal-term-of-use" */ '../Checkout/ModalTermsOfUse');

export default {
  mixins: [Register, PersonalDetails],
  components: {
    BaseTelephoneInput,
    ButtonFull,
    BaseCheckbox,
    BaseInput,
    SocialLogin,
    ModalTermsOfUse,
    Timer
  },
  data () {
    return {
      verificationCode: '',
      showModal: false,
      termsRequested: false,
      phoneMeta: {
        isValid: false
      },
      enableSendNewSMS: false
    }
  },
  validations () {
    return this.isLoggedIn ? {
      verificationCode: {
        required,
        minLength: minLength(4)
      }
    } : {
      email: {
        required,
        email
      },
      firstName: {
        minLength: minLength(2),
        required
      },
      lastName: {
        required
      },
      phoneNumber: {
        required: () => required && 'phoneNumber' in this.phoneMeta,
        isValid: () => this.phoneMeta.isValid
      },
      password: {
        minLength: minLength(8),
        required
      },
      rPassword: {
        required,
        sameAsPassword: sameAs('password')
      },
      conditions: {
        required
      }
    }
  },
  mounted () {
    if (this.$route.path === `/checkout/${this.getCurrentLocation.code}`) this.pasteDataFromCheckout()
  },
  computed: {
    ...mapGetters({
      getCurrentLocation: 'locations/getActiveLocation',
      isLoggedIn: 'user/isLoggedIn'
    }),
    phoneValidations () {
      return [
        {
          condition: this.$v.phoneNumber.$error && !this.$v.phoneNumber.required,
          text: this.$t('Field is required')
        },
        {
          condition: this.$v.phoneNumber.$error && this.$v.phoneNumber.required && !this.$v.phoneNumber.isValid,
          text: this.$t('Incorrect type of number')
        }
      ]
    }
  },
  methods: {
    ...mapActions({
      requestVerification: 'user/requestPhoneVerification',
      verify: 'user/verifyPhoneNumber'
    }),
    register () {
      if (this.$v.$invalid) {
        this.$v.$touch()
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: this.$t('Please fix the validation errors'),
          action1: { label: this.$t('OK') }
        })
        return
      }
      this.callRegister()
    },
    onSuccess () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'success',
        message: this.$t('You are logged in!'),
        action1: { label: this.$t('OK') }
      })
      this.requestVerification(this.phoneNumber)
    },
    verifyPhoneNumber () {
      if (!this.verificationCode || this.verificationCode.length < 4) {
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: this.$t('Verification code is not correct'),
          action1: { label: this.$t('OK') }
        })
        return
      }
      this.verify(this.verificationCode)
        .then(result => {
          let resMessage = result.result && result.result.message ? result.result.message : result.result
          let message = resMessage ? resMessage : 'Verification code is not correct'
          let status = result.result.status ? result.result.status : 'error'
          this.$store.dispatch('notification/spawnNotification', {
            type: status,
            message: this.$t(message),
            action1: { label: this.$t('OK') }
          })
          this.close()
        })
        .catch(error => {
          this.$store.dispatch('notification/spawnNotification', {
            type: 'error',
            message: this.$t('Something went wrong'),
            action1: { label: this.$t('OK') }
          })
          console.error(error)
        })
    },
    onFailure (result) {
      let message = 'Unable to login. Please, contact support!'
      if (result && result.result && result.result.message) {
        message = result.result.message
      } else if (result && result.result) {
        message = result.result
      }
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t(message),
        action1: { label: this.$t('OK') }
      })
    },
    openModal () {
      if (!this.termsRequested) {
        this.termsRequested = true
        return
      }
      this.$bus.$emit('modal-toggle', 'modal-terms')
      this.$bus.$emit('modal-in-modal')
      this.showModal = true
    },
    pasteDataFromCheckout () {
      this.email = this.personalDetails.emailAddress
      this.firstName = this.personalDetails.firstName
      this.lastName = this.personalDetails.lastName
      this.phoneNumber = this.personalDetails.phoneNumber
    },
    updatePhoneNumber (value) {
      this.phoneMeta = value;

      if ('formattedNumber' in value) {
        this.phoneNumber = value.formattedNumber;
      }
    },
    sendSMSAgain () {
      if (this.enableSendNewSMS) {
        let phone = this.phone ? this.phone : null
        this.requestVerification(phone)
        this.$bus.$emit('start-timer', 300)
        this.enableSendNewSMS = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .modal-content {
    @media (max-width: 400px) {
      padding-left: 20px;
      padding-right: 20px;
    }
  }

  .link {
    color: #000;
    text-decoration: underline;
  }

  .tel-input {
    min-height: 4.5rem;
  }
  .send-sms {
    text-decoration: underline;
    text-align: center;
    margin-bottom: 30px;
    color: #11bb2e;
    cursor: pointer;
  }

  .disabled {
    color: gray;
    cursor: grab;
  }
</style>
